import { Dialog, DialogContent } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Formik } from "formik";
import React from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { COMPANY_PERMISSIONS } from "@/constants/modules.permissions";
import useHasNoPermission from "@/hooks/use-check-permissions";
import { IClosedDays } from "@/interfaces/closed-days.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";

import {
  buildClosedDaysFormSchema,
  buildClosedDaysInitialData,
  mapClosedDaysFormToInput,
} from "./form-data";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  messages: ConfigurationTabMessages;
  isLoading: boolean;
  selected?: IClosedDays;
  onSubmit: (values: ReturnType<typeof mapClosedDaysFormToInput>) => void;
};

function ClosedDaysDialog({ isOpen, onClose, messages, isLoading, selected, onSubmit }: Props) {
  const hasNoPermissionToEdit = useHasNoPermission(COMPANY_PERMISSIONS.EDIT);
  const isEditing = !!selected;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <Formik
          enableReinitialize
          initialValues={buildClosedDaysInitialData(selected)}
          validationSchema={buildClosedDaysFormSchema(messages)}
          onSubmit={(values) => onSubmit(mapClosedDaysFormToInput(values))}
        >
          {({ values, errors, handleChange, setFieldValue, submitForm }) => (
            <MDBox p={2}>
              <MDTypography variant="h5" fontWeight="bold" mb={3}>
                {isEditing ? messages.closedDaysFormEditTitle : messages.closedDaysFormCreateTitle}
              </MDTypography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <DatePicker
                    label={messages.closedDaysDateLabel}
                    value={values.closedDay}
                    onChange={(value) => setFieldValue("closedDay", value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "standard",
                        error: !!errors.closedDay,
                        required: true,
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormField
                    name="description"
                    label={messages.closedDaysDescriptionLabel}
                    value={values.description}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.description}
                    required
                    variant="standard"
                  />
                </Grid>
              </Grid>

              <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton variant="outlined" color="dark" onClick={onClose}>
                  {messages.closedDaysCancelButton}
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={submitForm}
                  isLoading={isLoading}
                  disabled={hasNoPermissionToEdit}
                >
                  {messages.closedDaysSaveButton}
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default ClosedDaysDialog;
